/**
 * The campaign request form's values, and their conversion into the API's request.
 *
 * People type amounts in whole tokens, times as dates, and lists one item per line. The API takes
 * base units, ISO 8601 instants and arrays. `toRequest` converts and then checks the result with
 * the same schema and rules as the API (@morcast/campaigns), so the form shows every problem
 * before anything is sent. `formFromRequest` goes the other way, so that a brand can send an
 * earlier request again.
 */

import {
  type AcceptedToken,
  type BrandAssetType,
  type CampaignRequestInput,
  campaignDayMs,
  campaignRequestSchema,
  checkCampaignTerms,
  DAY_MS,
  formatTokenAmount,
  hasShortDays,
  MAX_CAMPAIGN_DAYS,
  MAX_X_CAMPAIGN_DAYS,
  MIN_LEAD_TIME_DAYS,
  type Platform,
  parseTokenAmount,
  YOUTUBE_TIME_ZONE,
  type YoutubeFormat,
  youtubeWindow,
  zonedDate,
  zonedMidnight,
} from "@morcast/campaigns";
import type { z } from "zod";

import type { BrandKitAsset, CampaignRequest } from "./requests.js";

/** An uploaded image of the brand kit, with a preview for the form. */
export interface BrandKitImage {
  label: string;
  sha256: string;
  mediaType: BrandAssetType;
  /** An address the browser shows the image from. */
  preview: string;
}

/** Everything the form holds, as typed. */
export interface RequestForm {
  brandName: string;
  contactEmail: string;
  platform: Platform;
  format: YoutubeFormat;
  /** Lowercase token address. */
  token: string;
  /** Budget in whole tokens, e.g. "100,000". */
  budget: string;
  target: string;
  /** X: start and end in UTC, as `YYYY-MM-DDTHH:mm`. */
  startAt: string;
  endAt: string;
  /** YouTube: first and final reporting dates in Los Angeles, as `YYYY-MM-DD`. */
  firstDate: string;
  finalDate: string;
  submissionLimit: string;
  title: string;
  description: string;
  disclosure: string;
  /** Lists, one item per line. */
  requiredHashtags: string;
  requiredMentions: string;
  requiredLinks: string;
  requiredText: string;
  semanticRequirements: string;
  visualRequirements: string;
  prohibitedContent: string;
  allowedEdits: string;
  /** The brand kit's uploaded images. */
  assets: BrandKitImage[];
}

/** An empty form. `token` is the first accepted token, if any. */
export function emptyForm(tokens: readonly AcceptedToken[]): RequestForm {
  return {
    brandName: "",
    contactEmail: "",
    platform: "X",
    format: "DEDICATED",
    token: tokens[0]?.address ?? "",
    budget: "",
    target: "",
    startAt: "",
    endAt: "",
    firstDate: "",
    finalDate: "",
    submissionLimit: "3",
    title: "",
    description: "",
    disclosure: "#ad",
    requiredHashtags: "",
    requiredMentions: "",
    requiredLinks: "",
    requiredText: "",
    semanticRequirements: "",
    visualRequirements: "",
    prohibitedContent: "",
    allowedEdits: "",
    assets: [],
  };
}

/**
 * A form filled in with a request's terms and brief: to send it again as a new request, changed
 * after a rejection, or to edit it or ask for a change of it (D25). For a new request, the dates
 * stay only while their start leaves the lead time a new request needs. Editing and changing keep
 * them, for the brand to keep or change. The budget stays only in a token still accepted. The
 * brand chooses the others again.
 *
 * @param tokens The tokens accepted for new campaigns.
 * @param now The current time, for the lead time.
 * @param assetUrl Where the browser shows an image of the brand kit from.
 * @param keepDates Whether the request's own dates stay, however soon they start.
 */
export function formFromRequest(
  request: CampaignRequest,
  tokens: readonly AcceptedToken[],
  now: Date,
  assetUrl: (asset: BrandKitAsset) => string,
  { keepDates = false }: { keepDates?: boolean } = {},
): RequestForm {
  const empty = emptyForm(tokens);
  const token = tokens.find((entry) => entry.address === request.token.address.toLowerCase());
  const leadTimeLeft =
    keepDates ||
    Date.parse(request.startAt) >= now.getTime() + MIN_LEAD_TIME_DAYS * campaignDayMs();
  const { brief } = request;
  return {
    ...empty,
    brandName: request.brandName,
    contactEmail: request.contactEmail,
    platform: request.platform,
    format: request.format ?? empty.format,
    token: token?.address ?? empty.token,
    budget: token ? formatTokenAmount(BigInt(request.budget), token.decimals) : "",
    target: BigInt(request.target).toLocaleString("en-US"),
    ...(leadTimeLeft ? windowDates(request) : {}),
    submissionLimit: String(request.submissionLimit),
    title: brief.title,
    description: brief.description,
    disclosure: brief.disclosure,
    requiredHashtags: brief.requiredHashtags.join("\n"),
    requiredMentions: brief.requiredMentions.join("\n"),
    requiredLinks: brief.requiredLinks.join("\n"),
    requiredText: brief.requiredText.join("\n"),
    semanticRequirements: brief.semanticRequirements.join("\n"),
    visualRequirements: brief.visualRequirements.join("\n"),
    prohibitedContent: brief.prohibitedContent.join("\n"),
    allowedEdits: brief.allowedEdits,
    assets: (brief.assets ?? []).map((asset) => ({ ...asset, preview: assetUrl(asset) })),
  };
}

/** A request's window as the form's fields hold it (the inverse of `timeWindow`). */
function windowDates(
  request: Pick<CampaignRequest, "platform" | "startAt" | "endAt">,
): Partial<RequestForm> {
  if (request.platform === "X") {
    return { startAt: request.startAt.slice(0, 16), endAt: request.endAt.slice(0, 16) };
  }
  // A YouTube window runs from the first date's midnight in Los Angeles to the midnight after the
  // final date, so half a day before its end is on the final date.
  return {
    firstDate: zonedDate(new Date(request.startAt), YOUTUBE_TIME_ZONE),
    finalDate: zonedDate(new Date(Date.parse(request.endAt) - DAY_MS / 2), YOUTUBE_TIME_ZONE),
  };
}

/** Result of converting the form: the request to send, or every problem found. */
export type FormResult =
  | { ok: true; request: CampaignRequestInput }
  | { ok: false; problems: string[] };

/** Names of request fields, for messages about them. */
const FIELD_LABELS: Record<string, string> = {
  brandName: "Brand name",
  contactEmail: "Contact email",
  token: "Token",
  budget: "Budget",
  target: "Target",
  startAt: "Start",
  endAt: "End",
  submissionLimit: "Submissions per account",
  "brief.title": "Title",
  "brief.description": "Description",
  "brief.disclosure": "Disclosure",
  "brief.requiredHashtags": "Hashtags",
  "brief.requiredMentions": "Mentions",
  "brief.requiredLinks": "Links",
  "brief.requiredText": "Required phrases",
  "brief.semanticRequirements": "Content requirements",
  "brief.visualRequirements": "Visual requirements",
  "brief.prohibitedContent": "Prohibited content",
  "brief.allowedEdits": "Allowed edits",
  "brief.assets": "Brand kit",
  "brief.assets.label": "Brand kit image name",
};

/**
 * Converts the form into a request and checks it.
 *
 * @param tokens The tokens accepted for new campaigns.
 * @param now The current time, for the rule on the start date.
 * @param minLeadTimeDays How soon the campaign may start, in days: by default the lead time of a
 *        new request. A change that keeps an accepted request's dates needs only a day to deposit.
 */
export function toRequest(
  form: RequestForm,
  tokens: readonly AcceptedToken[],
  now: Date,
  { minLeadTimeDays }: { minLeadTimeDays?: number } = {},
): FormResult {
  const problems: string[] = [];
  // Fields whose problem is already reported in plain words. The schema's message is skipped.
  const reported = new Set<string>();
  const token = tokens.find((entry) => entry.address === form.token);

  let budget = "";
  if (!token) {
    problems.push("Choose a token.");
    reported.add("token").add("budget");
  } else {
    try {
      budget = parseTokenAmount(form.budget, token.decimals).toString();
    } catch (error) {
      // The amount either is not a number or has more decimals than the token.
      problems.push(
        (error as Error).message.includes("decimal")
          ? `The budget can have at most ${token.decimals} decimal places.`
          : "Enter the budget as a number, like 100,000.",
      );
      reported.add("budget");
    }
  }

  let window = { startAt: "", endAt: "" };
  try {
    window = timeWindow(form);
  } catch {
    problems.push(
      form.platform === "X"
        ? "Choose both the start and the end."
        : "Choose the first and final reporting dates.",
    );
    reported.add("startAt").add("endAt");
  }

  const request: CampaignRequestInput = {
    brandName: form.brandName,
    contactEmail: form.contactEmail.trim(),
    platform: form.platform,
    format: form.platform === "YOUTUBE" ? form.format : null,
    token: form.token,
    budget,
    target: form.target.trim().replaceAll(",", ""),
    startAt: window.startAt,
    endAt: window.endAt,
    submissionLimit: Number(form.submissionLimit),
    brief: {
      title: form.title,
      description: form.description,
      disclosure: form.disclosure,
      requiredHashtags: lines(form.requiredHashtags),
      requiredMentions: lines(form.requiredMentions),
      requiredLinks: lines(form.requiredLinks),
      requiredText: lines(form.requiredText),
      semanticRequirements: lines(form.semanticRequirements),
      visualRequirements: lines(form.visualRequirements),
      prohibitedContent: lines(form.prohibitedContent),
      allowedEdits: form.allowedEdits,
      assets: form.assets.map(({ label, sha256, mediaType }) => ({
        label: label.trim(),
        sha256,
        mediaType,
      })),
    },
  };

  const parsed = campaignRequestSchema.safeParse(request);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const field = issue.path.filter((part) => typeof part === "string").join(".");
      if (!reported.has(field)) {
        problems.push(plainProblem(FIELD_LABELS[field] ?? field, issue));
      }
    }
  } else {
    problems.push(
      ...checkCampaignTerms(parsed.data, {
        now,
        tokens,
        ...(minLeadTimeDays === undefined ? {} : { minLeadTimeDays }),
      }),
    );
  }

  return problems.length > 0 ? { ok: false, problems: unique(problems) } : { ok: true, request };
}

/**
 * A schema problem as a sentence about its field, such as "Brand name needs at least 2
 * characters." zod's own messages are written for developers ("Too small: expected string to
 * have >=2 characters"), so the common ones are put in plain words. The schema's custom messages,
 * such as "must be an EVM address", are written to follow the field's name.
 */
function plainProblem(label: string, issue: z.core.$ZodIssue): string {
  const count = (n: number | bigint, one: string, many: string) =>
    `${n} ${Number(n) === 1 ? one : many}`;
  switch (issue.code) {
    case "too_small":
      if (issue.origin === "string") {
        return `${label} needs at least ${count(issue.minimum, "character", "characters")}.`;
      }
      if (issue.origin === "array") {
        return `${label} needs at least ${count(issue.minimum, "entry", "entries")}.`;
      }
      return `${label} must be at least ${issue.minimum}.`;
    case "too_big":
      if (issue.origin === "string") {
        return `${label} can have at most ${count(issue.maximum, "character", "characters")}.`;
      }
      if (issue.origin === "array") {
        return `${label} can have at most ${count(issue.maximum, "entry", "entries")}.`;
      }
      return `${label} can be at most ${issue.maximum}.`;
    case "invalid_format":
      if (issue.format === "email") {
        return `${label} must be an email address.`;
      }
      if (issue.format === "url") {
        return `${label} must be full web addresses that start with https://.`;
      }
      break;
  }
  return /^[a-z]/.test(issue.message) ? `${label} ${issue.message}.` : `${label}. ${issue.message}`;
}

/** The campaign window as ISO instants. Throws if a time or date is missing. */
function timeWindow(form: RequestForm): { startAt: string; endAt: string } {
  if (form.platform === "X") {
    if (!form.startAt || !form.endAt) throw new Error("missing time");
    // The form's times have no zone. It labels them as UTC.
    return { startAt: `${form.startAt}:00Z`, endAt: `${form.endAt}:00Z` };
  }
  const { startAt, endAt } = youtubeWindow(form.firstDate, form.finalDate);
  return { startAt: startAt.toISOString(), endAt: endAt.toISOString() };
}

/** The dates the form's calendars offer, `YYYY-MM-DD`. `lastEnd` is absent until a start is chosen. */
export interface CalendarBounds {
  firstStart: string;
  firstEnd: string;
  lastEnd: string | undefined;
}

/**
 * The dates the form's calendars offer: a start no sooner than the lead time allows, and an end
 * from the start's date to the longest window. The rules still check the exact times.
 */
export function calendarBounds(form: RequestForm, now: Date): CalendarBounds {
  const lead = new Date(now.getTime() + MIN_LEAD_TIME_DAYS * campaignDayMs());
  if (form.platform === "X") {
    const firstStart = lead.toISOString().slice(0, 10);
    const start = form.startAt.slice(0, 10);
    return {
      firstStart,
      firstEnd: start || firstStart,
      // Short days end an X campaign within hours, which may cross midnight.
      lastEnd: start ? addDays(start, hasShortDays() ? 1 : MAX_X_CAMPAIGN_DAYS) : undefined,
    };
  }
  // A YouTube campaign starts at midnight in Los Angeles: the first one after the lead time.
  const leadDate = zonedDate(lead, YOUTUBE_TIME_ZONE);
  const firstStart =
    zonedMidnight(leadDate, YOUTUBE_TIME_ZONE) >= lead ? leadDate : addDays(leadDate, 1);
  const start = form.firstDate;
  return {
    firstStart,
    firstEnd: start || firstStart,
    lastEnd: start ? lastYoutubeDate(start) : undefined,
  };
}

/**
 * The last reporting date a YouTube campaign from `firstDate` can cover: `MAX_CAMPAIGN_DAYS` dates,
 * or one fewer when the window gains the hour that daylight saving time gives back.
 */
function lastYoutubeDate(firstDate: string): string {
  const longest = MAX_CAMPAIGN_DAYS * DAY_MS;
  const startAt = zonedMidnight(firstDate, YOUTUBE_TIME_ZONE).getTime();
  let last = addDays(firstDate, MAX_CAMPAIGN_DAYS - 1);
  while (youtubeWindow(firstDate, last).endAt.getTime() - startAt > longest) {
    last = addDays(last, -1);
  }
  return last;
}

/** A calendar date `days` after another, `YYYY-MM-DD`. */
function addDays(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** The non-empty lines of a text, trimmed. */
function lines(text: string): string[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
}

function unique(items: string[]): string[] {
  return [...new Set(items)];
}
